import { Play, Volume2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { emit } from "#/lib/analytics";

// Lives in src/lib/youtube.ts so non-UI code can parse a link without pulling
// in this component; re-exported for the callers that import it from here.
export { youTubeId } from "#/lib/youtube";

export interface ShortsPlayerProps {
	videoId: string;
	/**
	 * A 9:16 still. Null falls back to YouTube's own thumbnail, which in turn
	 * falls back to `fallbackPoster` — unlisted videos serve no public
	 * thumbnail, so that last step is a real code path, not just belt-and-braces.
	 */
	poster: string | null;
	fallbackPoster: string;
	/** Accessible name of the play button. */
	label: string;
	/** Small line rendered under the play button, e.g. the duration. */
	hint: string;
	/** Reported with the `video_play` event so multiple players stay distinct. */
	section?: string;
	/**
	 * Start playing on its own once scrolled into view. Browsers only permit
	 * this **muted** — an embed on our domain is judged against our domain's
	 * media engagement, which is zero for ad traffic, so unmuted autoplay is
	 * refused (and leaves YouTube's player on a spinner). The visitor gets a
	 * tap-to-unmute control instead. Ignored for visitors on Save-Data or
	 * reduced-motion, who keep the click-to-play facade.
	 */
	autoplay?: boolean;
	/** Label for the unmute control shown while autoplay is running muted. */
	unmuteLabel?: string;
}

/**
 * A vertical (9:16, YouTube-Shorts ratio) player rendered as a facade: the
 * poster and play button are plain DOM, and YouTube's iframe — with all the
 * script weight that comes with it — is only mounted once the visitor actually
 * asks to watch. Keeps the landing page's first load as light as it was before
 * the video existed.
 */
export function ShortsPlayer({
	videoId,
	poster,
	fallbackPoster,
	label,
	hint,
	section = "hero",
	autoplay = false,
	unmuteLabel,
}: ShortsPlayerProps) {
	const [playing, setPlaying] = useState(false);
	const [posterFailed, setPosterFailed] = useState(false);
	/** True only while an autoplayed video is still running silently. */
	const [muted, setMuted] = useState(false);
	const frameRef = useRef<HTMLDivElement>(null);
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const primaryPoster =
		poster ?? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
	// YouTube's 480×360 thumbnail pillarboxes a vertical source; `object-cover`
	// in a 9:16 box crops exactly the black bars away, leaving the real frame.
	const posterSrc = posterFailed ? fallbackPoster : primaryPoster;

	function play() {
		setPlaying(true);
		emit("video_play", {
			params: { video_id: videoId, section, content_name: label },
		});
	}

	// Autoplay: hold the iframe back until the player is actually on screen, so
	// a visitor who bounces above it never pays for YouTube's payload.
	useEffect(() => {
		if (!autoplay || playing) return;

		// Metered connections and reduced-motion both mean "don't start moving
		// and downloading on my behalf" — those visitors keep the facade.
		const saveData = (
			navigator as Navigator & { connection?: { saveData?: boolean } }
		).connection?.saveData;
		if (
			saveData ||
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			return;
		}

		const node = frameRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;
				observer.disconnect();
				setPlaying(true);
				setMuted(true);
				emit("video_autoplay", {
					meta: false,
					params: { video_id: videoId, section },
				});
			},
			{ threshold: 0.4 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [autoplay, playing, videoId, section]);

	/**
	 * Unmuting is a user gesture, so sound is allowed from here on. Driven over
	 * postMessage (the iframe carries `enablejsapi=1`) rather than by reloading
	 * the embed with `mute=0`, which would restart playback from zero.
	 */
	function unmute() {
		iframeRef.current?.contentWindow?.postMessage(
			JSON.stringify({ event: "command", func: "unMute", args: [] }),
			"*",
		);
		setMuted(false);
		emit("video_unmute", { params: { video_id: videoId, section } });
	}

	// `mute=1` is what makes autoplay permissible at all; a click-to-play start
	// is a user gesture and needs no such concession.
	const embedParams = new URLSearchParams({
		autoplay: "1",
		playsinline: "1",
		rel: "0",
		modestbranding: "1",
		enablejsapi: "1",
		...(muted ? { mute: "1" } : {}),
	});

	return (
		<div
			ref={frameRef}
			className="grain relative aspect-9/16 w-full overflow-hidden rounded-[2rem] bg-navy-900 shadow-lift ring-1 ring-white/12"
		>
			{playing ? (
				<>
					<iframe
						ref={iframeRef}
						src={`https://www.youtube-nocookie.com/embed/${videoId}?${embedParams}`}
						title={label}
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						allowFullScreen
						className="absolute inset-0 h-full w-full border-0"
					/>
					{muted && unmuteLabel ? (
						// Centred rather than along an edge: YouTube owns the top strip
						// (title) and the bottom strip (scrubber, fullscreen), and an
						// overlay there would block its own controls.
						<button
							type="button"
							onClick={unmute}
							aria-label={unmuteLabel}
							data-track="hero_video_unmute"
							data-track-type="video"
							data-track-section={section}
							className="group absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center px-4"
						>
							<span className="relative inline-flex">
								{/* Same pulse the WhatsApp float uses — a muted video looks
								    like a working video, so the way back to sound has to
								    keep asking for attention rather than sit still. */}
								<span
									aria-hidden
									className="absolute inset-0 rounded-full bg-gold animate-wa-ring"
								/>
								<span className="relative inline-flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-bold text-navy shadow-gold animate-wa-pump transition-transform group-hover:scale-105 group-hover:[animation-play-state:paused]">
									<Volume2 className="h-4 w-4 flex-none" />
									{unmuteLabel}
								</span>
							</span>
						</button>
					) : null}
				</>
			) : (
				<button
					type="button"
					onClick={play}
					aria-label={label}
					data-track="hero_video_play"
					data-track-type="video"
					data-track-section={section}
					data-track-label="shorts_player"
					className="group absolute inset-0 h-full w-full cursor-pointer"
				>
					<img
						src={posterSrc}
						alt=""
						onError={() => setPosterFailed(true)}
						className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
					/>
					<span
						aria-hidden
						className="absolute inset-0 bg-linear-to-t from-navy-900 via-navy-900/25 to-navy-900/40"
					/>

					{/* The play button is centred rather than pinned to the bottom of
					    the frame: on a short phone a 9:16 box runs past the fold, and a
					    bottom-anchored button would sit off-screen, leaving the poster
					    looking like a plain image. The centre stays visible. */}
					<span className="absolute inset-0 flex items-center justify-center">
						<span className="relative flex h-16 w-16 items-center justify-center sm:h-[4.5rem] sm:w-[4.5rem]">
							<span
								aria-hidden
								className="absolute inset-0 rounded-full bg-white/80 animate-wa-ring"
							/>
							<span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white text-navy shadow-lift transition-transform duration-300 group-hover:scale-110 sm:h-[4.5rem] sm:w-[4.5rem]">
								{/* A play triangle always points right, RTL included — it's a
								    media convention, not a reading direction. `translate-x` is
								    physical (unlike `ms-*`), which is what makes it hold in
								    both directions; the nudge is optical centring, since a
								    triangle looks off-centre when geometrically centred. */}
								<Play className="h-6 w-6 translate-x-0.5 fill-current sm:h-7 sm:w-7" />
							</span>
						</span>
					</span>

					<span className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 p-5">
						<span className="text-center text-sm font-bold text-white drop-shadow-sm">
							{label}
						</span>
						{hint ? (
							<span className="rounded-full bg-white/12 px-3 py-1 text-xs font-semibold text-white/80 backdrop-blur-sm">
								{hint}
							</span>
						) : null}
					</span>
				</button>
			)}
		</div>
	);
}
